import React, { useState, useEffect } from "react";
import {
    RefreshCw, ChevronsLeft, ChevronsRight, ChevronLeft, ChevronRight,
    ShieldPlus, Search, Columns3, Eye, EyeOff, Check, ChevronUp, ChevronDown,
    Loader2
} from "lucide-react";
import { motion } from "framer-motion";
import baseapi from "../../services/baseapi";
import { showError } from "../../utils/toast";
import { ItemsPerPageDropdown } from "../../components/ItemsPerPageDropdown ";
import { useClientLevel } from "../../hooks/useClientLevel";

import { useNavigate } from "react-router-dom";
import OSPatchDetails from "./OSPatchDetails";

interface OSUpdateInfo {
    name: string;
    title: string;
    kb_ids: string;
    cve_ids: string;
    os_type: string;
    severity: string;
    update_id: string;
    new_version: string | null;
    pkg_manager: string;
    reboot_required: boolean;
}

interface OSPatchJob {
    id: string;
    job_id: string;
    organization_id: string;
    client_id: string;
    job_type: string;
    status: string;
    metrics: {
        total: number;
        initiated: number;
        running: number;
        failed: number;
        successed: number;
    };
    details: {
        [key: string]: {
            status: string;
            hostname: string;
            info: any;
        };
    };
    parameters: {
        updates: OSUpdateInfo[];
        reboot_time?: string;
        reboot_policy: string;
        reboot_required: boolean;
    };
    created: string;
    updated: string;
    is_deleted: boolean;
}

interface ApiResponse {
    total: number;
    data: OSPatchJob[];
    status: boolean;
}

const getOsLogo = (osType: string) => {
    const normalized = osType?.toLowerCase() || "";
    if (normalized === "windows") return "/windows.png";
    if (normalized === "macos" || normalized === "mac") return "/mac.png";
    if (normalized === "linux") return "/linux.svg";
    return "";
};

const OSPatchManagement: React.FC = () => {
    const [showDetails, setShowDetails] = useState(false);
    const [selectedPatch, setSelectedPatch] = useState<OSPatchJob | null>(null);
    const [patches, setPatches] = useState<OSPatchJob[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);

    const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({ key: 'created', direction: 'desc' });
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    const navigate = useNavigate();

    const level = useClientLevel();
    const clientId = level?.id;

    // Column visibility
    const [showColumnFilter, setShowColumnFilter] = useState(false);
    type ColumnKey = 'updates' | 'os' | 'reboot' | 'total' | 'initiated' | 'running' | 'failed' | 'successed' | 'status' | 'created';
    const [visibleColumns, setVisibleColumns] = useState<Record<ColumnKey, boolean>>({
        updates: true,
        os: true,
        reboot: true,
        total: true,
        initiated: true,
        running: true,
        failed: true,
        successed: true,
        status: true,
        created: true
    });

    const columnOptions: { key: ColumnKey; label: string }[] = [
        { key: 'updates', label: 'Updates' },
        { key: 'os', label: 'OS Type' },
        { key: 'reboot', label: 'Reboot Policy' },
        { key: 'total', label: 'Total' },
        { key: 'initiated', label: 'Init' },
        { key: 'running', label: 'Run' },
        { key: 'failed', label: 'Fail' },
        { key: 'successed', label: 'Succ' },
        { key: 'status', label: 'Status' },
        { key: 'created', label: 'Created' },
    ];

    const fetchPatches = async () => {
        setLoading(true);
        try {
            const skip = (currentPage - 1) * itemsPerPage;
            const params: any = {
                skip: skip,
                limit: itemsPerPage,
                sort_by: sortConfig.key,
                sort_order: sortConfig.direction,
            };



            const queryConditions: any = { job_type: 'os_patch' };
            if (clientId && level?.name !== 'global') queryConditions.client_id = clientId;
            if (debouncedSearch) {
                queryConditions.$or = [
                    { "status": { "$ilike": `%${debouncedSearch}%` } },
                    { "job_id": { "$ilike": `%${debouncedSearch}%` } }
                ];
            }
            params.query = JSON.stringify(queryConditions);

            const response = await baseapi.get<ApiResponse>("/agent_patch_jobs", { params });
            if (response.data.status) {
                const parsedData = (response.data.data || []).map((job: any) => {
                    try {
                        return {
                            ...job,
                            metrics: typeof job.metrics === 'string' ? JSON.parse(job.metrics) : job.metrics,
                            details: typeof job.details === 'string' ? JSON.parse(job.details) : job.details,
                            parameters: typeof job.parameters === 'string' ? JSON.parse(job.parameters) : job.parameters,
                        };
                    } catch (e) {
                        console.error("Failed to parse job fields", e);
                        return job;
                    }
                });
                setPatches(parsedData);
                setTotal(response.data.total || 0);
            }
        } catch (error: any) {
            showError(error.message || "Failed to fetch OS patches");
            setPatches([]);
            setTotal(0);
        } finally {
            setLoading(false);
            setIsRefreshing(false);
        }
    };

    useEffect(() => {
        if (clientId) {
            fetchPatches();
        } else {
            navigate("/dashboard");
        }
    }, [clientId, currentPage, itemsPerPage, sortConfig, debouncedSearch]);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchTerm);
            setCurrentPage(1);
        }, 500);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    const handleRefresh = () => {
        setIsRefreshing(true);
        fetchPatches();
    };

    const handleSort = (key: string) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
        setCurrentPage(1);
    };

    const handlePageChange = (page: number) => {
        const totalPages = Math.ceil(total / itemsPerPage);
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const handleJobClick = (patch: OSPatchJob) => {
        setSelectedPatch(patch);
        setShowDetails(true);
    };

    const formatDate = (dateString: string) => {
        if (!dateString) return "-";
        try {
            const utcString = dateString.endsWith("Z") ? dateString : `${dateString}Z`;
            const date = new Date(utcString);
            return date.toLocaleString("en-US", {
                year: "numeric", month: "short", day: "numeric",
                hour: "2-digit", minute: "2-digit", hour12: true,
            });
        } catch {
            return "Invalid Date";
        }
    };

    const getStatusBadge = (status: string) => {
        const statusColors: { [key: string]: string } = {
            initiated: "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-500/10 dark:text-blue-400 dark:border-blue-500/20",
            completed: "bg-green-50 text-green-700 border-green-200 dark:bg-green-500/10 dark:text-green-400 dark:border-green-500/20",
            failed: "bg-red-50 text-red-700 border-red-200 dark:bg-red-500/10 dark:text-red-500 dark:border-red-500/20 shadow-[0_0_5px_rgba(239,68,68,0.1)]",
            running: "bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-500/10 dark:text-yellow-400 dark:border-yellow-500/20",
        };
        const statusLower = (status || "").toLowerCase();
        return (
            <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border ${statusColors[statusLower] || "bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-500/10 dark:text-gray-200 dark:border-gray-500/20"}`}>
                <div className={`w-1.5 h-1.5 rounded-full ${statusLower === 'completed' ? 'bg-green-500 dark:bg-green-400' : statusLower === 'failed' ? 'bg-red-500' : 'bg-blue-500 dark:bg-blue-400'}`} />
                <span className="text-[9px] font-bold uppercase tracking-wider">{status.replace(/_/g, " ") || "Unknown"}</span>
            </div>
        );
    };

    const totalPages = Math.ceil(total / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = Math.min(startIndex + itemsPerPage, total);

    return (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="p-3 pb-20 bg-gray-50 dark:bg-gray-900 min-h-screen transition-colors duration-200 max-w-full overflow-x-hidden">
            <div className="w-full max-w-full min-w-0 mx-auto">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
                    <div className="flex items-center gap-2">
                        <div className="bg-orange-600 p-1.5 rounded shadow-lg">
                            <ShieldPlus className="w-4 h-4 text-white" />
                        </div>
                        <div className="flex items-center gap-2">
                            <h1 className="font-bold text-lg text-gray-900 dark:text-gray-200 leading-none">OS Patching Jobs</h1>
                            {!loading && <span className="text-lg font-bold text-gray-700 dark:text-gray-200">({total})</span>}
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <button onClick={handleRefresh} className="p-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 transition-all shadow-sm shrink-0" title="Refresh patches">
                            <RefreshCw className={`w-3.5 h-3.5 ${isRefreshing ? 'animate-spin' : ''}`} />
                        </button>
                        <div className="relative w-full sm:w-64">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 w-3.5 h-3.5" />
                            <input type="text" placeholder="Search OS patches..." className="w-full bg-white dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg pl-8 pr-3 py-1.5 text-[11px] text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:border-orange-500 outline-none transition-all shadow-sm" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                        </div>
                        <div className="relative shrink-0">
                            <button onClick={() => setShowColumnFilter(!showColumnFilter)} className="p-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 transition-all flex items-center gap-2 shadow-sm">
                                <Columns3 className="w-3.5 h-3.5" />
                                <span className="text-[10px] font-bold uppercase tracking-wider">Columns</span>
                            </button>
                            {showColumnFilter && (
                                <div className="absolute top-full mt-2 right-0 w-52 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl z-[110] p-1.5">
                                    <div className="px-3 py-1.5 text-[9px] font-bold text-gray-900 dark:text-gray-500 uppercase border-b border-gray-200 dark:border-gray-800 mb-1">Toggle Columns</div>
                                    {columnOptions.map((option) => (
                                        <button key={option.key} onClick={() => setVisibleColumns(prev => ({ ...prev, [option.key]: !prev[option.key] }))} className="w-full flex items-center justify-between px-3 py-2 text-[10px] font-bold uppercase text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors">
                                            <div className="flex items-center gap-2.5">
                                                {visibleColumns[option.key] ? <Eye className="w-3 h-3 text-orange-500" /> : <EyeOff className="w-3 h-3 text-gray-400 dark:text-gray-600" />}
                                                {option.label}
                                            </div>
                                            <div className={`w-3 h-3 rounded border flex items-center justify-center ${visibleColumns[option.key] ? 'bg-orange-600 border-orange-600' : 'border-gray-300 dark:border-gray-700'}`}>
                                                {visibleColumns[option.key] && <Check className="w-2.5 h-2.5 text-white" />}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Table Section */}
                <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700 rounded-t-xl overflow-hidden shadow-sm dark:shadow-2xl transition-colors duration-200">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-[10px] border-collapse">
                            <thead className="bg-gray-50 dark:bg-gray-800/80 sticky top-0 z-10">
                                <tr className="border-b border-gray-200 dark:border-gray-700/50">
                                    {columnOptions.map(({ key, label }) => (
                                        visibleColumns[key] && (
                                            <th key={key} onClick={() => handleSort(key)} className={`px-3 py-2 text-[9px] font-bold text-gray-700 dark:text-gray-200 uppercase tracking-widest cursor-pointer select-none hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors ${key.match(/total|initiated|running|failed|successed/) ? 'text-center' : ''}`}>
                                                <div className={`flex items-center gap-1 ${key.match(/total|initiated|running|failed|successed/) ? 'justify-center' : ''}`}>
                                                    {label}
                                                    {sortConfig.key === key ? (sortConfig.direction === 'asc' ? <ChevronUp size={10} /> : <ChevronDown size={10} />) : <ChevronDown size={10} className="opacity-20" />}
                                                </div>
                                            </th>
                                        )
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/30">
                                {loading ? (
                                    <tr>
                                        <td colSpan={Object.values(visibleColumns).filter(Boolean).length} className="py-20 text-center">
                                            <div className="flex flex-col items-center gap-2">
                                                <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
                                                <span className="text-[10px] font-bold uppercase tracking-widest text-gray-600 dark:text-gray-200 italic">Finding Patches...</span>
                                            </div>
                                        </td>
                                    </tr>
                                ) : patches.length === 0 ? (
                                    <tr>
                                        <td colSpan={Object.values(visibleColumns).filter(Boolean).length} className="py-20 text-center text-gray-500 dark:text-gray-400 font-bold uppercase tracking-widest italic">
                                            No OS patches found
                                        </td>
                                    </tr>
                                ) : (
                                    patches.map((patch) => {
                                        const updates = patch.parameters?.updates || [];
                                        const firstUpdate = updates[0];
                                        const moreUpdates = updates.length > 1 ? ` (+${updates.length - 1} more)` : "";
                                        const osType = firstUpdate?.os_type || "-";

                                        return (
                                            <tr key={patch.id || patch.job_id} className="hover:bg-gray-50/80 dark:hover:bg-gray-800/60 transition-colors group">
                                                {visibleColumns.updates && (
                                                    <td className="px-3 py-1.5">
                                                        <button onClick={() => handleJobClick(patch)} className="text-orange-600 dark:text-orange-400 font-bold cursor-pointer hover:underline text-left">
                                                            {firstUpdate ? `${firstUpdate.name}${moreUpdates}` : "No Updates"}
                                                        </button>
                                                    </td>
                                                )}
                                                {visibleColumns.os && (
                                                    <td className="px-3 py-1.5 uppercase font-medium text-gray-700 dark:text-gray-300">
                                                        <div className="flex items-center gap-2">
                                                            {getOsLogo(osType) && <img src={getOsLogo(osType)} alt="" className="w-3.5 h-3.5" />}
                                                            {osType}
                                                        </div>
                                                    </td>
                                                )}
                                                {visibleColumns.reboot && (
                                                    <td className="px-3 py-1.5">
                                                        <span className={`px-1.5 py-0.5 rounded text-[8px] font-black uppercase tracking-tighter ${patch.parameters?.reboot_policy === 'scheduled' ? 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-500/10 dark:text-purple-400 dark:border-purple-500/20' : 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-500/10 dark:text-blue-400 dark:border-blue-500/20'}`}>
                                                            {patch.parameters?.reboot_policy || "-"}
                                                        </span>
                                                    </td>
                                                )}
                                                {visibleColumns.total && <td className="px-3 py-1.5 text-center font-bold text-gray-900 dark:text-gray-200">{patch.metrics?.total || 0}</td>}
                                                {visibleColumns.initiated && <td className="px-3 py-1.5 text-center font-bold text-blue-600 dark:text-blue-400">{patch.metrics?.initiated || 0}</td>}
                                                {visibleColumns.running && <td className="px-3 py-1.5 text-center font-bold text-yellow-600 dark:text-yellow-400">{patch.metrics?.running || 0}</td>}
                                                {visibleColumns.failed && <td className="px-3 py-1.5 text-center font-bold text-red-600 dark:text-red-500">{patch.metrics?.failed || 0}</td>}
                                                {visibleColumns.successed && <td className="px-3 py-1.5 text-center font-bold text-green-600 dark:text-green-400">{patch.metrics?.successed || 0}</td>}
                                                {visibleColumns.status && <td className="px-3 py-1.5">{getStatusBadge(patch.status)}</td>}
                                                {visibleColumns.created && <td className="px-3 py-1.5 text-gray-600 dark:text-gray-300 font-mono text-[9px]">{formatDate(patch.created)}</td>}
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Footer */}
                <div className="bg-white dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-b-xl px-3 py-1 flex items-center justify-between shadow-sm transition-colors duration-200">
                    <div className="flex items-center gap-4">
                        <ItemsPerPageDropdown value={itemsPerPage} onChange={(val: any) => { setItemsPerPage(val); setCurrentPage(1); }} />
                        <span className="text-[10px] text-gray-500 dark:text-gray-400 font-bold uppercase tracking-widest"><span className="text-gray-700 dark:text-gray-200">{total > 0 ? startIndex + 1 : 0}</span> - <span className="text-gray-700 dark:text-gray-200">{endIndex}</span> OF {total}</span>
                    </div>
                    <div className="flex items-center gap-1">
                        <button onClick={() => handlePageChange(1)} disabled={currentPage === 1} className="p-1.5 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-30 transition-colors"><ChevronsLeft className="w-4 h-4" /></button>
                        <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="p-1.5 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-30 transition-colors"><ChevronLeft className="w-4 h-4" /></button>
                        <div className="px-3 py-1 bg-orange-600 rounded-lg text-white text-[11px] font-bold mx-1.5 shadow-md shadow-orange-900/20">{currentPage}</div>
                        <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages || totalPages === 0} className="p-1.5 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-30 transition-colors"><ChevronRight className="w-4 h-4" /></button>
                        <button onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages || totalPages === 0} className="p-1.5 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-30 transition-colors"><ChevronsRight className="w-4 h-4" /></button>
                    </div>
                </div>

                <OSPatchDetails isOpen={showDetails} onClose={() => { setShowDetails(false); setSelectedPatch(null); }} patchData={selectedPatch} />
            </div>
        </motion.div>
    );
};

export default OSPatchManagement;